import { useCallback, useEffect } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { QUERY_PARAMS } from '@/constants';
import type { Cart } from '@/modules/cart/domain/Cart';
import { useGetTBKToken } from '@/modules/perseo-billing/hooks/use-get-tbk-token';
import { useMutateConfirmOrderIntent } from '@/modules/perseo-billing/hooks/use-mutate-confirm-order-intent';
import {
  getPaymentsInteractionsOfOrder,
  hasWebpayCencoPaymentError
} from '@/modules/perseo-billing/hooks/utils';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { ErrorCodes } from '@/modules/perseo-billing/utils/error-handler/index';

const RETRY_CONFIG = {
  retry: 3,
  retryDelay: 5000
};

const buildCleanUrl = (location: Location): string => {
  const searchParams = new URLSearchParams(location.search);
  const deviceQueryParam = searchParams.get(QUERY_PARAMS.DEVICE);

  const newSearchParams = new URLSearchParams();
  if (deviceQueryParam) {
    newSearchParams.set(QUERY_PARAMS.DEVICE, deviceQueryParam);
  }

  const pathname = location.pathname;
  const paramsString = newSearchParams.toString();
  if (paramsString) return `${pathname}?${paramsString}`;

  return pathname;
};

const hasPaymentErrorForOrder = (cart: Cart, orderId: string): boolean => {
  const interactions = getPaymentsInteractionsOfOrder(cart, orderId);

  const hasCencoPaymentError = hasWebpayCencoPaymentError(interactions);
  if (hasCencoPaymentError) {
    console.error(
      `Payment processing failed due to CencoPaymentError for order: ${orderId}`
    );
    console.log('Canceling confirmation process due to payment error.');
  }

  return hasCencoPaymentError;
};

const useConfirmOrderIntent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { paymentError } = useAppSelector((state) => state.billing);
  const cart = useAppSelector((state) => state.cart.data);

  const tbkToken = useGetTBKToken();
  const retryConfig = !tbkToken ? RETRY_CONFIG : {};
  const { mutateAsync, isLoading, isSuccess, isError } =
    useMutateConfirmOrderIntent(id ?? '', retryConfig);

  const isCancelModalOpen = paymentError?.errorCode === ErrorCodes.CHKPY210090;
  const shouldNotProceed = isLoading || isSuccess || isError;

  const cleanQueryParams = useCallback(() => {
    const cleanedUrl = buildCleanUrl(location);
    navigate(cleanedUrl, { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    if (!id || !tbkToken || shouldNotProceed || !cart || !cart?.id) return;

    const hasPreviousPaymentError = hasPaymentErrorForOrder(cart, id);
    if (hasPreviousPaymentError) return; // * do not proceed if the cart has payment errors for the order

    const confirmOrderIntent = async () => {
      cleanQueryParams();

      await mutateAsync({
        paymentProcessor: PaymentProcessor.WEBPAY,
        orderNumber: id,
        token: tbkToken
      });
    };
    confirmOrderIntent();
  }, [cart, cleanQueryParams, id, mutateAsync, shouldNotProceed, tbkToken]);

  useEffect(() => {
    if (isCancelModalOpen && tbkToken) {
      cleanQueryParams();
    }
  }, [cleanQueryParams, isCancelModalOpen, tbkToken]);
};

export default useConfirmOrderIntent;
